import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import PasswordlessAuth from '../auth/PasswordlessAuth';
import UserProfile from '../user/UserProfile';

export default function TabNavigation({ activeTab, setActiveTab, isAdmin, setIsAdmin, currentTheme }) {
  const { isAuthenticated, signOut } = useAuth();
  const { isUserAuthenticated, getCurrentUser } = useEmailAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Check if debug mode is enabled
  const [debugMode, setDebugMode] = useState(() => {
    return localStorage.getItem('admin_debug_mode') === 'true';
  });

  // Helper function to render theme-based title icon
  const renderTitleIcon = () => {
    if (!currentTheme?.titleIcon) {
      return '🎃'; // Fallback to default jack-o-lantern
    }

    if (currentTheme.titleIcon.type === 'emoji') {
      return currentTheme.titleIcon.value;
    } else if (currentTheme.titleIcon.type === 'image') {
      return (
        <img
          src={currentTheme.titleIcon.value}
          alt="Theme icon"
          className="w-8 h-8 object-contain"
          style={{ filter: 'drop-shadow(0 0 4px rgba(255, 165, 0, 0.5))' }}
        />
      );
    }

    return '🎃'; // Fallback
  };

  // Listen for debug mode changes and check on every render.
  // Intentionally has no dependency array - it polls localStorage each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const currentDebugMode = localStorage.getItem('admin_debug_mode') === 'true';
    if (currentDebugMode !== debugMode) {
      setDebugMode(currentDebugMode);
    }
  });

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'admin_debug_mode') {
        setDebugMode(e.newValue === 'true');
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Debug auth state only when debug mode is enabled - moved after tabs definition

  // NOTE: this component used to keep a `forceUpdate` counter and listen for the
  // window `auth-force-update` event to stay in step with other copies of the
  // auth state. Auth now lives in a single provider, so a change re-renders every
  // consumer directly and none of that is needed. The clear-email-user-on-forced-
  // signout behaviour moved into AuthContext, where it belongs.

  // Close mobile menu when tab changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [activeTab]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);
  const allTabs = [
    { id: 'schedule', label: '📅 Schedule', icon: '📅' },
    { id: 'collection', label: '🎬 Collection', icon: '🎬' },
    { id: 'viewer', label: '👁️ Viewer', icon: '👁️' },
    { id: 'reviews', label: '📝 Reviews', icon: '📝' },
    { id: 'survey', label: '🗳️ Survey', icon: '🗳️' },
    { id: 'settings', label: '⚙️ Settings', icon: '⚙️' }
  ];

  // Tab access control:
  // - Admin: All tabs (including Settings, Schedule, Collection)
  // - Authenticated (email-only or full): Viewer, Reviews, Survey
  // - Unauthenticated: Viewer, Reviews, Survey (public content)
  const tabs = isAdmin ? allTabs : allTabs.filter(tab => 
    isUserAuthenticated 
      ? ['viewer', 'reviews', 'survey'].includes(tab.id)
      : ['viewer', 'reviews', 'survey'].includes(tab.id)
  );


  return (
    <>
      {/* Desktop Navigation */}
      <div className="app-nav hidden lg:flex items-center justify-between mb-6">
        {/* App Title */}
        <div className="flex items-center space-x-4">
          <h1 className="text-3xl font-bold text-orange-300 slasher-title flex items-center gap-3">
            {renderTitleIcon()} Calendar of Horror
          </h1>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-orange-600 text-white border-2 border-orange-400'
                  : 'bg-gray-800 text-orange-300 border-2 border-orange-600 hover:bg-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Authentication Status */}
        <div className="flex items-center space-x-3">
          {isUserAuthenticated ? (
            <AuthenticatedStatus 
              currentUser={getCurrentUser()}
              isAuthenticated={isAuthenticated}
              onSignOut={async () => {
                console.log('Sign out initiated', { isAuthenticated, isUserAuthenticated });

                // Clear only Supabase AUTH tokens, NOT configuration
                const supabaseAuthKeys = Object.keys(localStorage).filter(key =>
                  (key.includes('sb-') && (key.includes('auth-token') || key.includes('session'))) ||
                  key.includes('auth-token') ||
                  key === 'supabase.auth.token' ||
                  key.startsWith('supabase_auth_')
                );
                console.log('Clearing Supabase auth keys (preserving config):', supabaseAuthKeys);
                supabaseAuthKeys.forEach(key => localStorage.removeItem(key));

                // Then attempt Supabase sign out (asynchronous)
                if (isAuthenticated) {
                  try {
                    await signOut();
                    console.log('Supabase sign out completed');
                  } catch (error) {
                    console.log('Supabase sign out failed, but email user already cleared:', error);
                  }
                }
                
                console.log('Sign out process completed');
              }}
              onOpenProfile={() => setShowUserProfile(true)}
            />
          ) : (
            <UnauthenticatedStatus onSignIn={() => setShowAuthModal(true)} />
          )}
          
          {/* Admin Toggle - debug only, and only for a real verified session.
              This previously keyed off isUserAuthenticated, which is true for
              unverified Quick Join users, so anyone who set admin_debug_mode in
              devtools was handed an admin ON/OFF switch. */}
          {isAuthenticated && debugMode && (
            <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-orange-600">
              <span className="text-orange-300 font-medium text-sm">Admin:</span>
              <button
                onClick={() => setIsAdmin(!isAdmin)}
                className={`px-2 py-1 rounded-md font-medium transition-colors text-sm ${
                  isAdmin
                    ? 'bg-red-600 text-white hover:bg-red-500'
                    : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                }`}
              >
                {isAdmin ? 'ON' : 'OFF'}
              </button>
              <span className="text-orange-400 text-xs">🐛</span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="app-nav lg:hidden mb-6">
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-4">
          {/* App Title (Compact) */}
          <h1 className="text-xl font-bold text-orange-300 slasher-title flex items-center gap-2">
            <span className="text-2xl">{renderTitleIcon()}</span> Calendar of Horror
          </h1>

          {/* Mobile Menu Button & Auth */}
          <div className="flex items-center space-x-3">
            {/* Authentication Status (Compact) */}
            <div className="flex items-center space-x-2">
              {isUserAuthenticated ? (
                <MobileAuthenticatedStatus 
                  currentUser={getCurrentUser()}
                  isAuthenticated={isAuthenticated}
                  onSignOut={async () => {
                    // Same sign out logic as desktop
                    const supabaseKeys = Object.keys(localStorage).filter(key =>
                      key.includes('supabase') || key.includes('sb-') || key.includes('auth-token')
                    );
                    supabaseKeys.forEach(key => localStorage.removeItem(key));

                    if (isAuthenticated) {
                      try {
                        await signOut();
                      } catch (error) {
                        console.log('Supabase sign out failed:', error);
                      }
                    }
                  }}
                  onOpenProfile={() => setShowUserProfile(true)}
                    />
              ) : (
                <MobileUnauthenticatedStatus onSignIn={() => setShowAuthModal(true)} />
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <div className="mobile-menu-container relative">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg bg-gray-800 text-orange-300 border-2 border-orange-600 hover:bg-gray-700 transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>

              {/* Mobile Dropdown Menu */}
              {isMobileMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-gray-800 border-2 border-orange-600 rounded-lg shadow-xl z-50">
                  <div className="p-2 space-y-1">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg font-medium transition-colors ${
                          activeTab === tab.id
                            ? 'bg-orange-600 text-white'
                            : 'text-orange-300 hover:bg-gray-700'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                    
                    {/* Admin Toggle in Mobile Menu - Only visible to authenticated users in debug mode */}
                    {isUserAuthenticated && debugMode && (
                      <div className="border-t border-orange-600 pt-2 mt-2">
                        <div className="flex items-center justify-between px-3 py-2">
                          <span className="text-orange-300 font-medium text-sm">Admin Mode:</span>
                          <button
                            onClick={() => setIsAdmin(!isAdmin)}
                            className={`px-2 py-1 rounded-md font-medium transition-colors text-sm ${
                              isAdmin
                                ? 'bg-red-600 text-white hover:bg-red-500'
                                : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                            }`}
                          >
                            {isAdmin ? 'ON' : 'OFF'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Tab Indicator */}
        <div className="flex items-center justify-center">
          <div className="bg-gray-800 border-2 border-orange-600 rounded-lg px-4 py-2">
            <span className="text-orange-300 font-medium">
              {tabs.find(tab => tab.id === activeTab)?.label || 'Unknown Tab'}
            </span>
          </div>
        </div>
      </div>

      {/* Sign-in. One modal, one flow: an emailed 6-digit code or Google.
          The legacy password (AuthModal) and Quick Join (EmailSurveyAuth)
          modals were removed once the passwordless flow was verified live. */}
      <PasswordlessAuth
        isVisible={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      {/* User Profile Modal */}
      <UserProfile
        isVisible={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />
    </>
  );
}

// Component for authenticated users
const AuthenticatedStatus = ({ currentUser, isAuthenticated, onSignOut, onOpenProfile }) => {
  return (
    <div className="flex items-center space-x-3">
      {/* Profile Picture */}
      <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-600 border-2 border-orange-400 flex-shrink-0">
        {currentUser?.profilePicture ? (
          <img
            src={currentUser.profilePicture}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-orange-400">
            👤
          </div>
        )}
      </div>
      
      <div className="flex flex-col">
        <span className="text-orange-300 font-medium text-sm">
          Signed in as
        </span>
        <span className="text-orange-200 text-xs">
          {currentUser?.name || currentUser?.email || 'Unknown User'}
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={onOpenProfile}
          className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded transition-colors"
          title="Edit Profile"
        >
          Edit
        </button>
        
        <button
          onClick={onSignOut}
          className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded transition-colors"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

// Component for unauthenticated users
// One way in. There is no separate "Quick Join" vs "Full Sign In" any more -
// sign-in is passwordless (emailed code or Google), and the first use of an
// address creates the account.
const UnauthenticatedStatus = ({ onSignIn }) => {
  return (
    <div className="flex items-center space-x-3">
      <button
        onClick={onSignIn}
        className="bg-orange-600 hover:bg-orange-500 text-white px-4 py-1.5 rounded-lg font-bold transition-colors text-sm"
      >
        Sign In
      </button>
    </div>
  );
};

// Mobile version of authenticated status (more compact)
const MobileAuthenticatedStatus = ({ currentUser, onSignOut, onOpenProfile }) => {
  return (
    <div className="flex items-center space-x-2">
      {/* Compact Profile Picture */}
      <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-600 border border-orange-400 flex-shrink-0">
        {currentUser?.profilePicture ? (
          <img
            src={currentUser.profilePicture}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-orange-400 text-xs">
            👤
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-1">
        <button
          onClick={onOpenProfile}
          className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-1 py-0.5 rounded transition-colors"
          title="Edit Profile"
        >
          Edit
        </button>
        
        <button
          onClick={onSignOut}
          className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-1 py-0.5 rounded transition-colors"
          title="Sign Out"
        >
          Out
        </button>
      </div>
    </div>
  );
};

// Mobile version of unauthenticated status (more compact)
const MobileUnauthenticatedStatus = ({ onSignIn }) => {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={onSignIn}
        className="bg-orange-600 hover:bg-orange-500 text-white px-3 py-1 rounded text-xs font-bold transition-colors"
      >
        Sign In
      </button>
    </div>
  );
};
